import { RelayClient, RelayerTxType, type RelayerTransactionResponse } from '@polymarket/builder-relayer-client'
import type { BuilderConfig, BuilderHeaderPayload } from '@polymarket/builder-signing-sdk'
import type { JsonRpcSigner } from '@ethersproject/providers'
import type { Wallet } from '@ethersproject/wallet'

const VERSION = '1.0.1'
const POLYGON_CHAIN_ID = 137
const POLYGON_CHAIN_HEX = '0x89'
const POLYGON_USDCE = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'
const RELAYER_URL = 'https://relayer-v2.polymarket.com/'
const DEFAULT_WORKER_URL = 'https://polymarket-portfolio.snowboard3.workers.dev'
const ADDRESS = /^0x[a-fA-F0-9]{40}$/
const sessionMemory = new Map<string, Session>()

export type EthersV5Signer = Wallet | JsonRpcSigner
export type WalletKind = 'deposit' | 'safe' | 'proxy'
export type WithdrawStage = 'validating' | 'authorizing' | 'quoting' | 'signing' | 'submitting' | 'confirming' | 'complete' | 'failed'

export interface WithdrawStatus {
  stage: WithdrawStage
  message: string
}

export interface WithdrawOptions {
  signer: EthersV5Signer
  amount: string | number
  recipient: string
  workerUrl?: string
  timeoutMs?: number
  onStatus?: (status: WithdrawStatus) => void
}

export interface PositionalWithdrawOptions {
  workerUrl?: string
  timeoutMs?: number
  onStatus?: (status: WithdrawStatus) => void
}

export type WithdrawResult =
  | {
      success: true
      status: 'success' | 'pending'
      owner: string
      polymarketWallet: string
      amountBaseUnit: string
      recipient: string
      quoteSource: 'fun' | 'relay'
      transactionId: string
      transactionHash: string
      signaturesRequested: number
      message: string
    }
  | {
      success: false
      status: 'failed'
      stage: WithdrawStage
      code: string
      reason: string
    }

interface Session {
  token: string
  expiresAt: number
}

interface AccountResponse {
  profile: { proxyWallet?: string | null } | null
}

interface PortfolioResponse {
  available: number
  availableBaseUnit: string
}

interface QuoteResponse {
  quoteSource: 'fun' | 'relay'
  quoteId: string
  transactions: Array<{ to: string; data: string; value: string }>
}

class RemoteBuilderConfig {
  constructor(
    private token: string,
    private readonly workerUrl: string,
    private readonly refreshToken: () => Promise<string>,
  ) {}

  async generateBuilderHeaders(method: string, path: string, body?: string, timestamp?: number): Promise<BuilderHeaderPayload> {
    const sign = (token: string) => request<BuilderHeaderPayload>(this.workerUrl, '/polymarket/sign', {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      body: JSON.stringify({ method, path, body, timestamp }),
    })
    try {
      return await sign(this.token)
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status !== 401) throw cause
      this.token = await this.refreshToken()
      return sign(this.token)
    }
  }

  isValid() { return true }
}

class WithdrawError extends Error {
  constructor(readonly code: string, message: string) {
    super(message)
  }
}

class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

function workerBase(value?: string) {
  const base = (value || DEFAULT_WORKER_URL).trim().replace(/\/+$/, '')
  if (!/^https?:\/\//i.test(base)) throw new WithdrawError('INVALID_WORKER_URL', 'workerUrl must be an absolute HTTP(S) URL')
  const url = new URL(base)
  return url.pathname === '/' && !url.search && !url.hash ? `${base}/api` : base
}

async function request<T>(workerUrl: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${workerUrl}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; message?: string }
    throw new ApiError(response.status, body.error || body.message || `Worker request failed (${response.status})`)
  }
  return response.json() as Promise<T>
}

function emit(callback: WithdrawOptions['onStatus'], stage: WithdrawStage, message: string) {
  try { callback?.({ stage, message }) } catch { /* Consumer callbacks do not control withdrawal execution. */ }
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function parsePusdAmount(value: string | number) {
  const input = String(value).trim()
  if (!/^\d+(?:\.\d{1,6})?$/.test(input)) throw new WithdrawError('INVALID_AMOUNT', 'amount must be a positive pUSD value with at most 6 decimals')
  const [whole, fraction = ''] = input.split('.')
  const amount = BigInt(whole) * 1_000_000n + BigInt(fraction.padEnd(6, '0'))
  if (amount <= 0n) throw new WithdrawError('INVALID_AMOUNT', 'amount must be greater than zero')
  return amount.toString()
}

function sessionKey(workerUrl: string, owner: string) {
  return `polymarket-withdraw:${workerUrl}:${owner.toLowerCase()}`
}

function readSession(key: string) {
  const memory = sessionMemory.get(key)
  if (memory && memory.expiresAt > Date.now() + 5_000) return memory
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || 'null') as Session | null
    if (parsed?.token && parsed.expiresAt > Date.now() + 5_000) {
      sessionMemory.set(key, parsed)
      return parsed
    }
  } catch { /* sessionStorage may be disabled by the embedding page. */ }
  return null
}

function writeSession(key: string, session: Session) {
  sessionMemory.set(key, session)
  try { sessionStorage.setItem(key, JSON.stringify(session)) } catch { /* Memory cache remains available. */ }
}

async function relayerSession(workerUrl: string, signer: EthersV5Signer, force: boolean) {
  const owner = await signer.getAddress()
  const key = sessionKey(workerUrl, owner)
  if (!force) {
    const cached = readSession(key)
    if (cached) return { token: cached.token, signed: false }
  }
  const challenge = await request<{ challenge: string; token: string }>(workerUrl, `/polymarket/challenge?address=${encodeURIComponent(owner)}`)
  const signature = await signer.signMessage(challenge.challenge)
  const session = await request<Session>(workerUrl, '/polymarket/session', {
    method: 'POST',
    body: JSON.stringify({ address: owner, challengeToken: challenge.token, signature }),
  })
  writeSession(key, session)
  return { token: session.token, signed: true }
}

async function ensurePolygon(signer: EthersV5Signer) {
  if (await signer.getChainId() === POLYGON_CHAIN_ID) return
  const provider = signer.provider as unknown as { send?: (method: string, params: unknown[]) => Promise<unknown> } | undefined
  if (!provider?.send) throw new WithdrawError('WRONG_NETWORK', 'Signer must be connected to Polygon (chain 137)')
  await provider.send('wallet_switchEthereumChain', [{ chainId: POLYGON_CHAIN_HEX }])
  if (await signer.getChainId() !== POLYGON_CHAIN_ID) throw new WithdrawError('WRONG_NETWORK', 'Wallet did not switch to Polygon')
}

async function deployed(workerUrl: string, wallet: string, type: 'SAFE' | 'WALLET') {
  return (await request<{ deployed: boolean }>(workerUrl, `/deployed?address=${encodeURIComponent(wallet)}&type=${type}`)).deployed
}

async function resolveAccount(workerUrl: string, signer: EthersV5Signer, owner: string) {
  const { profile } = await request<AccountResponse>(workerUrl, `/account?address=${encodeURIComponent(owner)}`)
  if (profile?.proxyWallet && ADDRESS.test(profile.proxyWallet)) {
    const wallet = profile.proxyWallet
    const [deposit, safe] = await Promise.all([
      deployed(workerUrl, wallet, 'WALLET'),
      deployed(workerUrl, wallet, 'SAFE'),
    ])
    return { wallet, kind: deposit ? 'deposit' : safe ? 'safe' : 'proxy' } as const
  }

  const client = new RelayClient(RELAYER_URL, POLYGON_CHAIN_ID, signer)
  const wallet = await client.deriveDepositWalletAddress()
  if (!await deployed(workerUrl, wallet, 'WALLET')) {
    throw new WithdrawError('ACCOUNT_NOT_FOUND', 'The signer does not have an active Polymarket account')
  }
  return { wallet, kind: 'deposit' } as const
}

async function waitForRelay(workerUrl: string, quoteId: string, timeoutMs: number) {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const state = await request<Record<string, unknown>>(workerUrl, `/relay/status?requestId=${encodeURIComponent(quoteId)}`)
    const status = String(state.status || '').toLowerCase()
    if (status === 'success') return 'success' as const
    if (['failure', 'fallback', 'refund'].includes(status)) {
      throw new WithdrawError('RELAY_FAILED', String(state.details || state.message || `Relay reported ${status}`))
    }
    await sleep(3_000)
  }
  return 'pending' as const
}

function normalizeOptions(
  signerOrOptions: EthersV5Signer | WithdrawOptions,
  amount?: string | number,
  recipient?: string,
  options?: PositionalWithdrawOptions,
): WithdrawOptions {
  if ('signer' in signerOrOptions) return signerOrOptions
  return { signer: signerOrOptions, amount: amount ?? '', recipient: recipient ?? '', ...options }
}

export async function withdraw(options: WithdrawOptions): Promise<WithdrawResult>
export async function withdraw(signer: EthersV5Signer, amount: string | number, recipient: string, options?: PositionalWithdrawOptions): Promise<WithdrawResult>
export async function withdraw(
  signerOrOptions: EthersV5Signer | WithdrawOptions,
  amount?: string | number,
  recipient?: string,
  positionalOptions?: PositionalWithdrawOptions,
): Promise<WithdrawResult> {
  const options = normalizeOptions(signerOrOptions, amount, recipient, positionalOptions)
  let stage: WithdrawStage = 'validating'
  try {
    emit(options.onStatus, stage, 'Checking signer, account, balance, and destination')
    const signer = options.signer as EthersV5Signer & { _signTypedData?: (...args: unknown[]) => Promise<string> }
    if (!signer || typeof signer.getAddress !== 'function' || typeof signer.signMessage !== 'function' || typeof signer._signTypedData !== 'function' || !signer.provider) {
      throw new WithdrawError('INVALID_SIGNER', 'An ethers v5 Signer is required')
    }
    if (!ADDRESS.test(options.recipient)) throw new WithdrawError('INVALID_RECIPIENT', 'recipient must be a valid EVM address')
    const amountBaseUnit = parsePusdAmount(options.amount)
    const workerUrl = workerBase(options.workerUrl)
    await ensurePolygon(options.signer)
    const owner = await options.signer.getAddress()
    if (!ADDRESS.test(owner)) throw new WithdrawError('INVALID_SIGNER', 'Signer returned an invalid address')
    const account = await resolveAccount(workerUrl, options.signer, owner)
    const portfolio = await request<PortfolioResponse>(workerUrl, `/portfolio?wallet=${encodeURIComponent(account.wallet)}`)
    if (BigInt(portfolio.availableBaseUnit) < BigInt(amountBaseUnit)) {
      throw new WithdrawError('INSUFFICIENT_PUSD', `Available balance is ${portfolio.available} pUSD`)
    }

    stage = 'quoting'
    emit(options.onStatus, stage, 'Getting the Polygon USDC withdrawal route')
    const quote = await request<QuoteResponse>(workerUrl, '/bridge/quote', {
      method: 'POST',
      body: JSON.stringify({
        wallet: account.wallet,
        amountBaseUnit,
        recipientAddress: options.recipient,
        toChainId: String(POLYGON_CHAIN_ID),
        toTokenAddress: POLYGON_USDCE,
      }),
    })
    if (!quote.quoteId || quote.transactions.length < 2) throw new WithdrawError('INCOMPLETE_ROUTE', 'Withdrawal route is incomplete')

    stage = 'authorizing'
    emit(options.onStatus, stage, 'Checking relayer authorization')
    let signaturesRequested = 0
    const getToken = async (force: boolean) => {
      const session = await relayerSession(workerUrl, options.signer, force)
      if (session.signed) signaturesRequested += 1
      return session.token
    }
    const token = await getToken(false)
    const builder = new RemoteBuilderConfig(token, workerUrl, () => getToken(true))
    const relayType = account.kind === 'proxy' ? RelayerTxType.PROXY : RelayerTxType.SAFE
    // RelayClient only consumes generateBuilderHeaders() and isValid(); its nominal type also contains unused private members.
    const builderConfig = builder as unknown as BuilderConfig
    const client = new RelayClient(RELAYER_URL, POLYGON_CHAIN_ID, options.signer, builderConfig, relayType)

    stage = 'signing'
    emit(options.onStatus, stage, 'Confirm the withdrawal batch in your wallet')
    signaturesRequested += 1
    const calls = quote.transactions.map((transaction) => ({ target: transaction.to, to: transaction.to, data: transaction.data, value: transaction.value }))
    const deadline = Math.floor(Date.now() / 1_000 + 600).toString()
    const response: RelayerTransactionResponse = account.kind === 'deposit'
      ? await client.executeDepositWalletBatch(calls.map(({ target, data, value }) => ({ target, data, value })), account.wallet, deadline)
      : await client.execute(calls.map(({ to, data, value }) => ({ to, data, value })), 'External pUSD withdrawal')

    stage = 'confirming'
    emit(options.onStatus, stage, 'Waiting for Polygon confirmation')
    const confirmed = await response.wait()
    if (!confirmed) {
      const transactions = await response.getTransaction().catch(() => [])
      const latest = transactions[0]
      if (latest?.state === 'STATE_FAILED' || latest?.state === 'STATE_INVALID') {
        throw new WithdrawError('TRANSACTION_FAILED', `Polymarket relayer reported ${latest.state}`)
      }
      return {
        success: true,
        status: 'pending',
        owner,
        polymarketWallet: account.wallet,
        amountBaseUnit,
        recipient: options.recipient,
        quoteSource: quote.quoteSource,
        transactionId: response.transactionID,
        transactionHash: latest?.transactionHash || response.transactionHash || '',
        signaturesRequested,
        message: 'Withdrawal was submitted and is still pending Polygon confirmation',
      }
    }

    emit(options.onStatus, stage, 'Polygon confirmed; checking delivery')
    const delivery = await waitForRelay(workerUrl, quote.quoteId, options.timeoutMs ?? 360_000)
    stage = 'complete'
    emit(options.onStatus, stage, delivery === 'success' ? 'Withdrawal complete' : 'Withdrawal confirmed and delivery is pending')
    return {
      success: true,
      status: delivery,
      owner,
      polymarketWallet: account.wallet,
      amountBaseUnit,
      recipient: options.recipient,
      quoteSource: quote.quoteSource,
      transactionId: response.transactionID,
      transactionHash: confirmed.transactionHash || response.transactionHash || '',
      signaturesRequested,
      message: delivery === 'success' ? 'Withdrawal successful' : 'Polygon confirmed; destination delivery is still pending',
    }
  } catch (cause) {
    const reason = cause instanceof Error ? cause.message : 'Unknown withdrawal error'
    const rejected = /reject|denied|cancelled|canceled/i.test(reason)
    const code = cause instanceof WithdrawError ? cause.code : rejected ? 'USER_REJECTED' : cause instanceof ApiError ? `WORKER_${cause.status}` : 'WITHDRAWAL_FAILED'
    emit(options.onStatus, 'failed', reason)
    return { success: false, status: 'failed', stage, code, reason }
  }
}

export const version = VERSION
export const defaultWorkerUrl = DEFAULT_WORKER_URL
